import { ComponentIdEnum } from './../component-factory/ComponentIdEnum';
import { nodeIsSlot, judgeIsContainer } from './../utils/index';
import { toJS, makeAutoObservable } from 'mobx';
import React, { CSSProperties } from 'react';
import {
  ElementConfigType,
  StyleObject,
  PageConfigType,
  RemoteComponent,
  RemoteComponentType,
  styleRuleType,
} from './type';

export class PageEditorStore {
  configNode: ElementConfigType | null = null;
  hoverNode: ElementConfigType | null = null;
  saveNode: ElementConfigType | null = null; // “保存组件”弹窗的数据
  forceUpdate = 0;
  isDevelopment = true;
  isRenderHtml = true;
  model: 'elements' = 'elements'; // 当前模式
  folderId: number | null = null; //  文件id
  showRichTextEditorDialog = false; // 富文本编辑器弹窗
  showSaveComponentDialog = false; // 保存组件弹窗
  showRenameComponentDialog = false; // 重命名组件弹窗
  showAddStyleRuleDialog = false; // 新增组件样式规则弹窗
  currentStyle: StyleObject | null = null; // 当前选中的style规则
  remoteComponents: RemoteComponent[] = []; // 远程自定义组件
  remoteComponentTypes: RemoteComponentType[] = []; // 远程自定义组件的归属类型
  renameComponentId: RemoteComponent['id'];

  page: PageConfigType = this.createInitPage();

  dragStrategy = {
    targetIsParent: this.switchInnerTargetNode.bind(this),
    targetIsSlot: this.insertNodeToSlot.bind(this),
    inSameParent: this.switchSameParentNode.bind(this),
    moveCrossNode: this.switchCrossNodePlace.bind(this),
    hasSubChildrenNodes: this.moveHasSubChildrenNodes.bind(this),
    default: this.switchSlotNodePlace.bind(this),
  };

  constructor() {
    makeAutoObservable(this);
  }

  /** 创建基础的page对象 */
  createInitPage() {
    const nodeId = this.generateNodeId();
    return {
      elements: [
        {
          nodeId,
          parentNodeId: '0',
          componentId: 'body-component',
          componentName: '页面容器',
          hidden: true,
          isSlot: true,
          index: 1,
          config: {},
          styles: {},
          composeStyle: [this.createInitStyleRule(nodeId!)],
          privateSchema: {
            maxWidth: '1354px', // 初始值
          },
          elements: [],
        },
      ],
    };
  }

  /** 创建基础的基本容器对象 */
  createInitBasicEl(parentNode?: ElementConfigType, subElement?: ElementConfigType) {
    const nodeId = this.generateNodeId();
    return {
      nodeId,
      parentNodeId: parentNode?.nodeId ?? this.generateNodeId(),
      index: parentNode ? parentNode.elements?.length + 1 : 1,
      componentId: 'Basic-Component',
      componentName: '基本容器',
      isSlot: true,
      config: {},
      styles: {}, // 组件通用css
      composeStyle: [this.createInitStyleRule(nodeId!)],
      privateSchema: {}, // 组件自己的属性 不能通用的部分
      elements: subElement ? [subElement] : [],
    };
  }

  createEmptyElement(parentNode?: ElementConfigType): ElementConfigType {
    const nodeId = this.generateNodeId();
    return {
      nodeId,
      parentNodeId: parentNode?.nodeId ?? this.generateNodeId(),
      index: parentNode ? parentNode.elements?.length + 1 : 1,
      componentId: ComponentIdEnum.EMPTY_CONTAINER_COMPONENT,
      componentName: '空白容器',
      config: {},
      styles: {}, // 组件通用css
      composeStyle: [this.createInitStyleRule(nodeId!)],
      privateSchema: {}, // 组件自己的属性 不能通用的部分
      elements: [],
    };
  }

  setModel(model: 'elements') {
    this.model = model;
    this.forceUpdate++;
  }

  // 设置页面
  setPage(page: PageConfigType) {
    this.page = toJS(page);
  }
  /** 设置当前选中元素 */
  setConfigNode(node: ElementConfigType | null) {
    const nodeId = toJS(node)?.nodeId;
    const matchNode = nodeId ? this.loopSearchNode(this.page[this.model], nodeId) : null;
    if (matchNode) {
      if (!Array.isArray(matchNode.composeStyle) || !matchNode.composeStyle.length) {
        matchNode.composeStyle = [];
        matchNode.composeStyle.push(this.createInitStyleRule(nodeId!));
      }
      this.configNode = matchNode;
      this.setCurrentStyle(matchNode.composeStyle[0]);
    }
  }

  createInitStyleRule(nodeId: string, styles = {}): StyleObject {
    return {
      id: `default_${nodeId}`,
      name: '默认规则',
      type: 'DEFAULT',
      minWidth: 0,
      maxWidth: 0,
      rules: {
        ...styles,
      },
    };
  }

  /** 适应旧数据，把旧的样式数据加入到默认规则中 */
  updateDefaultRule(nodeId: string, styles = {}) {
    const matchNode = this.loopSearchNode(this.page[this.model], nodeId);
    if (!matchNode!.composeStyle) {
      matchNode!.composeStyle = [this.createInitStyleRule(nodeId, styles)];
    }
  }

  setFolderId(id: number | null) {
    this.folderId = id;
  }
  /** 当前hover元素 */
  setHoverNode(node: ElementConfigType | null) {
    this.hoverNode = node ? toJS(node) : node;
  }
  setSaveNode(node: ElementConfigType | null) {
    this.saveNode = node;
  }
  setIsDevelopment(preview: boolean) {
    this.isDevelopment = preview;
    this.forceUpdate++;
  }
  setIsRenderHtml(isRenderHtml = true) {
    this.isRenderHtml = isRenderHtml;
    this.forceUpdate++;
  }

  setShowRichTextEditorDialog(show: boolean) {
    this.showRichTextEditorDialog = show;
  }

  setShowRenameComponentDialog(show: boolean) {
    this.showRenameComponentDialog = show;
  }

  setShowSaveComponentDialog(show: boolean) {
    this.showSaveComponentDialog = show;
  }

  setShowAddStyleRuleDialog(show: boolean) {
    this.showAddStyleRuleDialog = show;
  }

  setCurrentStyle(style: StyleObject | null) {
    this.currentStyle = style;
  }

  // 设置 css 属性
  setConfigNodeStyle(style: StyleObject | null) {
    const nodeId = this.configNode?.nodeId;
    const matchNode = nodeId ? this.loopSearchNode(this.page[this.model], nodeId) : null;
    if (matchNode) {
      for (const item of matchNode.composeStyle) {
        if (item.id === this.currentStyle?.id) {
          Object.assign(item, style || {});
          this.setCurrentStyle(item);
          this.forceUpdate++;
          break;
        }
      }
    }
  }

  // 新建样式规则
  addStyleRule(type: styleRuleType = 'EMPTY') {
    const nodeId = this.configNode?.nodeId;
    const matchNode = nodeId ? this.loopSearchNode(this.page[this.model], nodeId) : null;
    if (matchNode) {
      const id = this.generateNodeId();

      switch (type) {
        case 'EMPTY':
          matchNode.composeStyle.push({
            id: `custom_${nodeId}_${id}`,
            name: '自定义规则',
            type: 'CUSTOM',
            minWidth: 0,
            maxWidth: 0,
            rules: {},
          });
          break;
        case 'MOBILE':
          matchNode.composeStyle.push({
            id: `custom_${nodeId}_${id}`,
            name: 'M端规则',
            type: 'CUSTOM',
            minWidth: 0,
            maxWidth: 750,
            rules: {},
          });
          break;
        case 'PC':
          matchNode.composeStyle.push({
            id: `custom_${nodeId}_${id}`,
            name: 'PC端规则',
            type: 'CUSTOM',
            minWidth: 750,
            maxWidth: 0,
            rules: {},
          });
          break;
        default:
          break;
      }

      this.configNode!.composeStyle = matchNode.composeStyle;
    }
  }

  // 删除样式规则
  removeStyleRule(styleId: string) {
    const nodeId = this.configNode?.nodeId;
    const matchNode = nodeId ? this.loopSearchNode(this.page[this.model], nodeId) : null;
    if (matchNode) {
      const targetIndex = matchNode.composeStyle.findIndex((item) => item.id === styleId);
      if (targetIndex < 0 || matchNode.composeStyle[targetIndex].type === 'DEFAULT') {
        return;
      }
      matchNode.composeStyle.splice(targetIndex, 1);

      // 删除后设置当前选中规则为被删除的前一个
      this.setCurrentStyle(matchNode.composeStyle[targetIndex - 1]);
    }
  }

  // 设置自己的特有 属性
  setConfigNodeOwnAttribute(privateSchema: CSSProperties) {
    if (this.configNode) {
      const { nodeId } = this.configNode;
      const matchNode = this.loopSearchNode(this.page[this.model], nodeId);
      if (matchNode) {
        matchNode.privateSchema = Object.assign({}, matchNode.privateSchema, privateSchema);
        this.forceUpdate++;
      }
    }
  }

  // 生成nodeId
  generateNodeId() {
    return `${new Date().getTime()}${Math.round(Math.random() * 1000000)}`;
  }

  /** 添加，参数index是用于节点的copy，不传则为正常的添加 */
  addNode(nodeId: string, newNode: ElementConfigType, index?: number) {
    const matchNode = this.loopSearchNode(this.page[this.model], nodeId);
    if (matchNode) {
      index ? matchNode.elements.splice(index, 0, newNode) : matchNode.elements.push(newNode);
      this.forceUpdate++;
    } else {
      console.warn('add 没找到对应Node: ', nodeId);
    }
  }

  copyNodeUnderSameParent(nodeId: string) {
    const matchNode = JSON.parse(JSON.stringify(this.loopSearchNode(this.page[this.model], nodeId)));
    const parentNode = JSON.parse(JSON.stringify(this.loopSearchNode(this.page[this.model], matchNode!.parentNodeId)));
    if (nodeIsSlot(parentNode) && parentNode!.componentId !== ComponentIdEnum.BODY_COMPONENT) {
      const truthParentNode = JSON.parse(
        JSON.stringify(this.loopSearchNode(this.page[this.model], parentNode!.parentNodeId)),
      );
      const nodeWrapper = this.createInitBasicEl(truthParentNode!);
      const newNode = this.copyNodesData(matchNode!, nodeWrapper.nodeId);
      nodeWrapper.elements.push(newNode);
      this.addNode(
        truthParentNode!.nodeId,
        nodeWrapper,
        this.loopSearcNodeIndex(truthParentNode!.elements, parentNode!.nodeId) + 1,
      );
    } else {
      const newNode = JSON.parse(JSON.stringify(this.copyNodesData(matchNode!, parentNode!.nodeId)));
      this.addNode(parentNode!.nodeId, newNode, this.loopSearcNodeIndex(parentNode!.elements, matchNode!.nodeId) + 1);
    }
  }

  /** 递归复制节点和其下面的内容 */
  copyNodesData(componentConfig: ElementConfigType, parentId: string): ElementConfigType {
    if (componentConfig.elements.length === 0) {
      return {
        ...componentConfig,
        nodeId: this.generateNodeId(),
        parentNodeId: parentId,
      };
    }
    const newNode = {
      ...componentConfig,
      nodeId: this.generateNodeId(),
      parentNodeId: parentId,
    };
    newNode.elements = newNode.elements.map((el) => this.copyNodesData(el, newNode.nodeId));
    return newNode;
  }

  /** 判断拖入的元素是哪种插槽，调用不同方法 */
  callNodesReplaceFunc(dragNodeId: string, targetNodeId: string) {
    let currentStrategy = 'default';

    const hasBasicComponents = [
      ComponentIdEnum.TAB_V2_A_COMPONENT,
      ComponentIdEnum.FEATURE_COMPONENT,
      ComponentIdEnum.COLUMNS_CONTAINER_COMPONENT,
      ComponentIdEnum.ROWS_CONTAINER_COMPONENT,
      ComponentIdEnum.MULTI_SUPER_TAB_COMPONENT,
      ComponentIdEnum.SINGLE_SUPER_TAB_COMPONENT,
    ];

    const matchTargetNode = this.loopSearchNode(this.page[this.model], targetNodeId);
    const matchDragNode = this.loopSearchNode(this.page[this.model], dragNodeId);

    if (!matchDragNode || !matchTargetNode) {
      return '找不到对应节点ID';
    }

    // Tab组件子元素不允许拖拽到其他组件里
    if (matchDragNode.componentId === ComponentIdEnum.TAB_V2_A_CHILD) {
      const tabList = [ComponentIdEnum.TAB_V2_A_CHILD, ComponentIdEnum.TAB_V2_A_COMPONENT];
      if (!tabList.includes(matchTargetNode.componentId as any)) {
        return 'Tab子元素只可在Tab内部操作';
      }
    }

    const matchDragNodeParent = this.loopSearcParentNode(this.page[this.model], matchDragNode.nodeId);

    if (matchTargetNode.elements.some((node) => `${node.nodeId}` === `${matchDragNode.nodeId}`)) {
      // 1. 拖拽目标元素是自己的父级
      currentStrategy = 'targetIsParent';
    } else if (nodeIsSlot(matchTargetNode as ElementConfigType)) {
      // 2. 拖拽目标元素的插槽（Basic-render组件）
      currentStrategy = 'targetIsSlot';
    } else if (matchDragNode.parentNodeId === matchTargetNode.parentNodeId) {
      // 3. 拖拽和目标元素属于同个父级下的同一层级（外层没有BasicRender）
      currentStrategy = 'inSameParent';
    } else if (hasBasicComponents.includes(matchTargetNode.componentId as any)) {
      if (matchDragNodeParent?.parentNodeId === matchTargetNode.nodeId) {
        currentStrategy = 'hasSubChildrenNodes';
      } else {
        currentStrategy = 'moveCrossNode';
      }
    }

    // eslint-disable-next-line no-console
    console.log('currentStrategy', currentStrategy);
    return this.dragStrategy[currentStrategy](matchDragNode, matchTargetNode);
  }

  /** 卖点组件子元素拖动到卖点组件的情况  */
  moveHasSubChildrenNodes(matchDragNode: ElementConfigType, matchTargetNode: ElementConfigType) {
    const matchDragNodeParent = this.loopSearcParentNode(this.page[this.model], matchDragNode.nodeId);

    const newNode = {
      ...matchDragNodeParent!,
      parentNodeId: matchTargetNode.nodeId,
    };
    const nodeIndex = this.loopSearcNodeIndex(matchTargetNode.elements, matchDragNodeParent!.nodeId);

    matchTargetNode.elements.unshift(newNode);
    matchTargetNode?.elements.splice(nodeIndex + 1, 1);
    return null;
  }

  /** 插入节点到插槽容器里 */
  insertNodeToSlot(matchDragNode: ElementConfigType, matchTargetNode: ElementConfigType) {
    if (matchDragNode?.parentNodeId === matchTargetNode?.nodeId) {
      return '已在当前父节点下';
    }
    const cloneDragNode: ElementConfigType = JSON.parse(JSON.stringify(matchDragNode));
    cloneDragNode.parentNodeId = matchTargetNode.nodeId;

    this.deleteNode(cloneDragNode.nodeId);
    matchTargetNode!.elements.push(cloneDragNode);
    return '插入成功';
  }

  /** 跨层级交换，例如将一个多列容器移动到一个多行容器，那多列则是添加到多行里 */
  switchCrossNodePlace(matchDragNode: ElementConfigType, matchTargetNode: ElementConfigType) {
    const matchDragNodeParent = this.loopSearcParentNode(this.page[this.model], matchDragNode.nodeId);
    const nodeWrapper = this.createInitBasicEl(matchTargetNode);
    const newNode = {
      ...matchDragNode,
      parentNodeId: judgeIsContainer(matchTargetNode) ? nodeWrapper.nodeId : matchTargetNode.nodeId,
    };
    nodeWrapper.elements.push(newNode);
    matchTargetNode.elements.unshift(judgeIsContainer(matchTargetNode) ? nodeWrapper : newNode);
    const nodeIndex = this.loopSearcNodeIndex(matchDragNodeParent!.elements, matchDragNode.nodeId);
    matchDragNodeParent?.elements.splice(nodeIndex, 1);
    return null;
  }

  /** 交换两个节点的位置 */
  switchSlotNodePlace(matchDragNode: ElementConfigType, matchTargetNode: ElementConfigType) {
    const matchTargetNodeParent = this.loopSearcParentNode(this.page[this.model], matchTargetNode.nodeId);
    const matchDragNodeParent = this.loopSearcParentNode(this.page[this.model], matchDragNode.nodeId);

    // Tab-V2
    if (matchDragNodeParent!.parentNodeId === matchTargetNodeParent!.parentNodeId) {
      this.switchSameParentNode(matchDragNodeParent!, matchTargetNodeParent!);
      return;
    }

    if (
      matchTargetNode.elements.every((node) => `${node.nodeId}` !== `${matchDragNode.nodeId}`) &&
      matchTargetNode.elements.length !== 0
    ) {
      this.switchCrossNodePlace(matchDragNode, matchTargetNode);
      return;
    }

    const targetNode: ElementConfigType = JSON.parse(JSON.stringify(matchTargetNode));
    const dragNode: ElementConfigType = JSON.parse(JSON.stringify(matchDragNode));
    targetNode.parentNodeId = matchDragNodeParent?.nodeId as string;
    dragNode.parentNodeId = matchTargetNodeParent?.nodeId as string;
    this.deleteNode(targetNode.nodeId);
    this.deleteNode(dragNode.nodeId);
    matchDragNodeParent?.elements.push(targetNode);
    matchTargetNodeParent?.elements.push(dragNode);
    return '节点位置排序';
  }

  /** 交换的节点在同一个父级 */
  switchSameParentNode(matchDragNode: ElementConfigType, matchTargetNode: ElementConfigType) {
    const parentNodes = this.loopSearcParentNode(this.page[this.model], matchDragNode.nodeId)!;
    const pageElements = parentNodes.elements;

    const targetNodeIndex = this.loopSearcNodeIndex(pageElements, `${matchTargetNode.nodeId}`);
    const dragNodeIndex = this.loopSearcNodeIndex(pageElements, `${matchDragNode.nodeId}`);

    if (targetNodeIndex > dragNodeIndex) {
      pageElements.splice(targetNodeIndex, 1, matchTargetNode, matchDragNode);

      pageElements.splice(dragNodeIndex, 1);
    } else {
      pageElements.splice(targetNodeIndex, 1, matchDragNode, matchTargetNode);

      pageElements.splice(dragNodeIndex + 1, 1);
    }
    return '节点位置排序';
  }

  /** 交换拖动节点是目标节点的子元素 */
  switchInnerTargetNode(matchDragNode: ElementConfigType, parentNodes: ElementConfigType) {
    const parentElements = parentNodes.elements;
    const dragNodeIndex = parentElements.findIndex((parentNode) => parentNode.nodeId === matchDragNode.nodeId);
    parentElements.splice(dragNodeIndex, 1);
    parentElements.unshift(matchDragNode);
    return '节点位置排序';
  }
  // 减少 删除最后一个 Node
  reduceNode(nodeId: string) {
    const matchNode = this.loopSearchNode(this.page[this.model], nodeId);
    if (matchNode) {
      matchNode.elements.pop();

      this.forceUpdate++;
    } else {
      console.warn('reduce 没找到对应Node: ', nodeId);
    }
  }

  /* 删除 */
  deleteNode(nodeId: string) {
    const matchNode = this.loopSearcParentNode(this.page[this.model], nodeId);
    if (matchNode) {
      matchNode.elements = matchNode?.elements?.filter((d) => d.nodeId !== nodeId);
      this.forceUpdate++;
    } else {
      console.warn('删除 没找到对应Node: ', nodeId);
    }
  }

  /* 寻找当前容器 */
  loopSearchNode(elements: ElementConfigType[], nodeId: string): ElementConfigType | null {
    for (const element of elements) {
      if (element.nodeId === nodeId) {
        return element;
      } else {
        const nextLoop = this.loopSearchNode(element.elements, nodeId);
        if (nextLoop) {
          return nextLoop;
        }
      }
    }
    return null;
  }

  /* 寻找父级 */
  loopSearcParentNode(elements: ElementConfigType[], nodeId: string): ElementConfigType | null {
    let parentNode: ElementConfigType | null = null;
    for (let i = 0; i < elements.length; i++) {
      const node = elements[i];
      if (node.elements) {
        if (node.elements.some((item) => item?.nodeId === nodeId)) {
          parentNode = node;
        } else if (this.loopSearcParentNode(node.elements, nodeId)) {
          parentNode = this.loopSearcParentNode(node.elements, nodeId);
        }
      }
    }
    return parentNode;
  }

  /** 判断节点是否位于容器之下 */
  searchNodeInnerContainer(element: ElementConfigType) {
    if (element.parentNodeIsBody) return false;
    const matchSlot = this.loopSearchNode(this.page[this.model], element.parentNodeId);

    if (!nodeIsSlot(matchSlot!) && matchSlot?.componentId === ComponentIdEnum.BODY_COMPONENT) {
      return false;
    }

    const parentNode = this.loopSearchNode(this.page[this.model], matchSlot!.parentNodeId);
    return judgeIsContainer(parentNode!);
  }
  /** 判断插槽是否位于容器之下 */
  judgeSlotInnerContainer(nodeId: string) {
    const currentElement = this.loopSearchNode(this.page[this.model], nodeId);

    if (
      !currentElement ||
      currentElement?.parentNodeIsBody ||
      currentElement?.componentId === ComponentIdEnum.BODY_COMPONENT
    ) {
      return false;
    }

    const parentNode = this.loopSearchNode(this.page[this.model], currentElement!.parentNodeId);
    return judgeIsContainer(parentNode!);
  }

  /* 寻找当前容器 在父级下一级排第几个(只适用在不是body-content下的其他元素) */
  loopSearcNodeIndex(elements: ElementConfigType[], nodeId: string, targetIsBody = false): number {
    let nodeIndex;
    if (targetIsBody) {
      nodeIndex = elements[0].elements.findIndex((d) => d.nodeId === nodeId);
    } else {
      nodeIndex = elements.findIndex((d) => d.nodeId === nodeId);
    }
    return nodeIndex;
  }

  setRemoteComponents(components: RemoteComponent[]) {
    this.remoteComponents = components;
  }

  setRemoteComponentTypes(types: RemoteComponentType[]) {
    this.remoteComponentTypes = types;
  }

  setRenameComponentId(id: RemoteComponent['id']) {
    this.renameComponentId = id;
  }
  resetDelComponentType(id: RemoteComponent['id']) {
    const delComponentList = this.remoteComponents.filter((component) => component.deleted);
    const searchComponent = delComponentList.find((component) => component.id === id)!;
    searchComponent.deleted = false;
    const preType = this.remoteComponentTypes.find((componentType) => componentType.id === searchComponent.typeId);
    return `撤回成功,组件回到${preType?.type}下的${preType?.subType}类目`;
  }
}

export const PageEditorStoreContext = React.createContext<PageEditorStore>(null as any);
